export type ExpirationMonthParts = {
  month: string;
  year: string;
};

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function validYearMonth(year: number, month: number): boolean {
  return Number.isInteger(year) && year >= 1000 && year <= 9999 && Number.isInteger(month) && month >= 1 && month <= 12;
}

function parseYearMonth(value: string): { year: number; month: number } | null {
  const trimmed = value.trim();

  const machineMonth = /^(\d{4})-(\d{2})$/.exec(trimmed);
  if (machineMonth) {
    const year = Number(machineMonth[1]);
    const month = Number(machineMonth[2]);
    return validYearMonth(year, month) ? { year, month } : null;
  }

  const displayMonth = /^(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (displayMonth) {
    const month = Number(displayMonth[1]);
    const year = Number(displayMonth[2]);
    return validYearMonth(year, month) ? { year, month } : null;
  }

  const legacyDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!legacyDate) return null;

  const year = Number(legacyDate[1]);
  const month = Number(legacyDate[2]);
  const day = Number(legacyDate[3]);
  if (!validYearMonth(year, month) || !Number.isInteger(day) || day < 1 || day > 31) return null;

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month };
}

export function normalizeExpirationDate(value: string | null | undefined): string | null {
  if (value == null || !value.trim()) return null;
  const parsed = parseYearMonth(value);
  if (!parsed) return null;

  const lastDay = new Date(Date.UTC(parsed.year, parsed.month, 0)).getUTCDate();
  return `${parsed.year}-${pad2(parsed.month)}-${pad2(lastDay)}`;
}

export function expirationMonthValue(value: string | null | undefined): string {
  const normalized = normalizeExpirationDate(value);
  return normalized ? normalized.slice(0, 7) : "";
}

export function formatExpirationMonth(value: string | null | undefined): string {
  const normalized = normalizeExpirationDate(value);
  return normalized ? `${normalized.slice(5, 7)}/${normalized.slice(0, 4)}` : "";
}

export function parseExpirationMonthParts(value: string | null | undefined): ExpirationMonthParts {
  const normalized = normalizeExpirationDate(value);
  if (!normalized) return { month: "", year: "" };
  return { month: normalized.slice(5, 7), year: normalized.slice(0, 4) };
}

export function expirationMonthPartsToValue(parts: ExpirationMonthParts): string | null {
  if (!parts.month || parts.year.length !== 4) return null;
  return normalizeExpirationDate(`${parts.year}-${parts.month.padStart(2, "0")}`);
}

export function areExpirationMonthPartsBlank(parts: ExpirationMonthParts): boolean {
  return !parts.month && !parts.year;
}

export function expirationMonthValidationMessage(
  parts: ExpirationMonthParts,
  options: { allowEmpty?: boolean; required?: boolean } = {}
): string {
  const allowEmpty = options.allowEmpty ?? !options.required;
  if (areExpirationMonthPartsBlank(parts)) return allowEmpty ? "" : "Enter an expiration month.";
  return expirationMonthPartsToValue(parts) ? "" : "Enter a valid expiration month.";
}
